<script setup lang="ts">
const props = defineProps({
  debounce: {
    default: 0,
    type: Number,
  },
  hiddenClass: {
    required: true,
    type: String,
  },
  once: {
    default: false,
    type: Boolean,
  },
  visibleClass: {
    required: true,
    type: String,
  },
})

const rootElement = useTemplateRef<HTMLElement>('rootElement')
const isVisible = useElementVisibility(rootElement)

const hasAnimated = ref(false)
const debounceTimer = ref<ReturnType<typeof useRafTimeout>>()
const debouncedVisible = ref(false)

const finalVisible = computed(() => {
  if (props.once && hasAnimated.value) {
    return true
  }

  return debouncedVisible.value
})

watch(
  isVisible,
  (newValue) => {
    if (debounceTimer.value) {
      debounceTimer.value.stop()
    }

    if (!props.debounce) {
      debouncedVisible.value = newValue

      return
    }

    if (!debouncedVisible.value) {
      debouncedVisible.value = newValue
    }
    else {
      debounceTimer.value = useRafTimeout(
        () => (debouncedVisible.value = newValue),
        props.debounce,
      )
    }
  },
)

watch(finalVisible, (newValue) => {
  if (newValue && !hasAnimated.value) {
    hasAnimated.value = true
  }
})
</script>

<template>
  <div ref="rootElement">
    <div
      :class="[
        $attrs.class,
        {
          [visibleClass]: finalVisible,
          [hiddenClass]: !finalVisible,
        },
      ]"
    >
      <slot :visible="finalVisible" />
    </div>
  </div>
</template>
